/**
 * Field Indicator State (ORCH-251, ORCH-266)
 *
 * Purpose: Computes the border-color indicator state for a single Create Media
 *          Plan field row, replacing the previous icon-based approach.
 *          Semantics are confidence-driven: green = high-confidence match;
 *          yellow = AI guess below threshold; red = required + no match;
 *          null = optional left blank (no indicator shown).
 * Architecture: Pure function — no JSX or React dependency. Callers
 *               (TemplateField, ExtraFields) apply the returned state as a
 *               border-color override via MUI sx and a Tooltip.
 * Dependencies: ./types (FieldMatchResult)
 */
import type { FieldMatchResult } from './types';

/**
 * Minimum confidence to show a green border (mirrors backend threshold).
 * Exported so callers (e.g. MaestroMappingsStepper) share one source of truth.
 */
export const MIN_CONFIDENCE_THRESHOLD = 0.6;

/** True when a field value is considered non-empty (used by indicator state). */
export const valuePresent = (v: unknown): boolean => {
  if (v === null || v === undefined || v === '') return false;
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === 'object' && 'has_files' in (v as Record<string, unknown>)) {
    return Boolean((v as { has_files?: boolean }).has_files);
  }
  return true;
};

/** Maps indicator state to MUI theme token border color. */
export const INDICATOR_BORDER: Record<'success' | 'warning' | 'error', string> = {
  success: 'success.main',
  warning: 'warning.main',
  error: 'error.main',
};

/**
 * Indicator mode (ORCH-266):
 * - 'none'            → no coloring (initial page load, before any user action)
 * - 'maestro-pending' → brief attached, Maestro's first pass hasn't returned yet.
 *                       Fields that already have a value (e.g. the brief field itself)
 *                       show their color; blank fields stay uncolored — Maestro hasn't
 *                       had a chance to say what it did or didn't find, so nothing is red.
 * - 'maestro'         → Maestro has returned real output (applied fields or a completed
 *                       pass) — full coloring, including red for required-and-blank.
 * - 'submitted'       → validate-on-submit coloring (no brief path: red only appears
 *                       after submit attempt)
 */
export type IndicatorMode = 'none' | 'maestro-pending' | 'maestro' | 'submitted';

export interface FieldIndicatorProps {
  fieldId: string;
  isRequired: boolean;
  indicatorMode: IndicatorMode;
  appliedFieldKeys: Set<string> | undefined;
  fieldResults: Record<string, FieldMatchResult> | undefined;
  /** Whether the field currently holds a non-empty value (manual entry counts). */
  hasValue?: boolean;
}

export interface FieldIndicatorState {
  /**
   * Border-color signal. Maps to MUI theme tokens:
   * success → success.main (green), warning → warning.main (amber),
   * error → error.main (red), null → no border override.
   */
  state: 'success' | 'warning' | 'error' | null;
  /** Tooltip text shown on hover. Null when no context to surface. */
  tooltipText: string | null;
}

const formatConfidence = (confidence: number): string => `${Math.round(confidence * 100)}%`;

/**
 * getFieldIndicatorState — maps Maestro match state to a border-color signal
 *
 * Confidence-driven semantics (ORCH-251, ORCH-266):
 * - success: matched + confidence ≥ threshold, or value present after unmatched
 * - warning: matched + confidence < threshold (AI guess — any field)
 * - error:   required field with no value and no match
 * - null:    optional field with no value (no indicator); or indicatorMode 'none';
 *            or required-and-blank while 'maestro-pending' (Maestro hasn't reported yet)
 *
 * Timing (ORCH-266): 'none' suppresses all coloring (page load). 'maestro-pending'
 * (brief attached, no Maestro output yet) colors only fields that already have a
 * value — required-and-blank stays uncolored rather than jumping straight to red,
 * since Maestro hasn't had a chance to say what it found. 'maestro' and 'submitted'
 * both apply the full coloring logic (including red for required-and-blank) — the
 * difference is only when each is activated (real Maestro output vs. submit attempt).
 *
 * Falls back to appliedFieldKeys inference for pending / missing explicit results
 * (ORCH-203 path), same as the previous icon implementation.
 */
export function getFieldIndicatorState({
  fieldId,
  isRequired,
  indicatorMode,
  appliedFieldKeys,
  fieldResults,
  hasValue = false,
}: FieldIndicatorProps): FieldIndicatorState {
  if (indicatorMode === 'none') return { state: null, tooltipText: null };

  const explicit = fieldResults?.[fieldId];
  const isApplied = appliedFieldKeys?.has(fieldId) === true;

  if (!hasValue) {
    // Maestro hasn't returned anything yet (brief just attached) — don't flag
    // required-and-blank as an error until there's an actual pass to judge against.
    if (isRequired && indicatorMode !== 'maestro-pending') {
      const couldNotFill = explicit?.status === 'unmatched';
      return {
        state: 'error',
        tooltipText: couldNotFill
          ? 'Required — Maestro could not fill this field'
          : 'Required — not filled yet',
      };
    }
    // Optional + no value → neutral; yellow is reserved for AI guesses only.
    return { state: null, tooltipText: null };
  }

  // Value present. Explicit ORCH-215 result decides the provenance shown;
  // 'pending' means not-yet-evaluated and falls through to the inference path.
  if (explicit && explicit.status !== 'pending') {
    if (explicit.status === 'matched') {
      const source = explicit.source_term ? ` — from "${explicit.source_term}"` : '';
      if (explicit.confidence >= MIN_CONFIDENCE_THRESHOLD) {
        return {
          state: 'success',
          tooltipText: `Filled by Maestro (${formatConfidence(explicit.confidence)} confidence)${source}`,
        };
      }
      return {
        state: 'warning',
        tooltipText: `AI guess (${formatConfidence(explicit.confidence)} confidence)${source} — please verify`,
      };
    }
    // unmatched but a value exists: the user fixed it by hand.
    return { state: 'success', tooltipText: 'Filled manually' };
  }

  // ORCH-203 inference fallback (older payloads / pending results).
  return {
    state: 'success',
    tooltipText: isApplied ? 'Filled by Maestro' : 'Filled',
  };
}
